import { buildMaskPath, setBackgroundMaskVisibility } from '@/infrastructure/engine2d/helpers/FabricBackgroundMaskHelper';
import { BACKGROUND_MASK_NAME, WORKING_ZONE_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { TRANSPARENT_BG_INDICATOR_COLOR } from '@/infrastructure/engine2d/engine2dConstants';
import { Group, Rect, type Canvas } from 'fabric';

type TBackgroundMaskServiceParams = {
    canvas: Canvas;
    getImageRenderHeight: () => number;
    getImageRenderWidth: () => number;
};

// Behavior-preserving extraction of the working-zone + background-mask
// concern previously embedded in FabricScene (addWorkingZone,
// setCanvasBackgroundColor, setMaskOpaque/Transparent for the mask side,
// keepBackgroundMaskUpFront). Working zone and mask are created together
// so they live in a single adapter — the mask geometry is derived from
// the working-zone contour.
class BackgroundMaskService {
    private readonly _canvas: Canvas;
    private readonly _getImageRenderHeight: () => number;
    private readonly _getImageRenderWidth: () => number;

    constructor(params: TBackgroundMaskServiceParams) {
        this._canvas = params.canvas;
        this._getImageRenderHeight = params.getImageRenderHeight;
        this._getImageRenderWidth = params.getImageRenderWidth;
    }

    initWorkingZone(): void {
        const workingZoneGroup = new Group([], {
            evented: false,
            selectable: false,
        });
        workingZoneGroup.set('objectCaching', false);
        workingZoneGroup.set('name', WORKING_ZONE_NAME);

        const contour = new Rect({
            fill: TRANSPARENT_BG_INDICATOR_COLOR,
            height: Math.floor(this._getImageRenderHeight()),
            left: 0,
            strokeWidth: 0,
            top: 0,
            width: Math.floor(this._getImageRenderWidth()),
        });

        contour.set('objectCaching', false);
        workingZoneGroup.add(contour);
        contour.set('top', Math.floor(contour.top));
        contour.set('left', Math.floor(contour.left));
        this._canvas.add(workingZoneGroup);

        const maskColor = this._canvas.backgroundColor || 'rgba(0,0,0,0.5)';
        const mask = buildMaskPath(contour, { fill: maskColor });
        mask.set('id', BACKGROUND_MASK_NAME);
        mask.set('top', Math.floor(mask.top + this._getImageRenderHeight() / 2));
        mask.set('left', Math.floor(mask.left + this._getImageRenderWidth() / 2));
        this._canvas.add(mask);
        this._canvas.bringObjectToFront(mask);
    }

    keepMaskUpFront(): void {
        const mask = this._canvas.getObjects().find((obj) => (obj as { id?: string }).id === BACKGROUND_MASK_NAME);
        if (!mask) {
            return;
        }
        this._canvas.bringObjectToFront(mask);
        this._canvas.renderAll();
    }

    setBackgroundColor(color: string): void {
        const workingZone = (this._canvas.getObjects('group') as Group[]).find((group) => group.get('name') === WORKING_ZONE_NAME);
        if (!workingZone) {
            console.warn('Working zone not found');
            return;
        }
        const contour = workingZone.getObjects()[0];
        if (!contour) {
            return;
        }
        contour.set('fill', color === 'transparent' || color === '' ? TRANSPARENT_BG_INDICATOR_COLOR : color);
        workingZone.set('dirty', true);
        this._canvas.requestRenderAll();
    }

    setMaskOpacity(opacity: number): void {
        setBackgroundMaskVisibility(this._canvas, opacity);
    }
}

export { BackgroundMaskService };
